/**
 * PaperStock の正本となる型定義。
 *
 * ここで定義した型は、日次パイプライン（Node）・公開 API（Cloudflare Worker）・
 * Web クライアントの3か所から同じファイルを import して共有する。
 * iOS / Android へ展開する際は src/contracts/schema.ts の JSON Schema から
 * Swift / Kotlin の型を生成する（scripts/paperstock export-schema）。
 */

/** 保存済みデータとの互換性を含む読者レベル。現在生成するのは beginner だけ。 */
export type Level = 'beginner' | 'intermediate' | 'expert';
export const LEVELS: readonly Level[] = ['beginner'];
/** Workerの引用同期境界でも参照する、現行テキスト生成契約。 */
export const CURRENT_TEXT_PROMPT_VERSION = 'PS-TEXT-2026-09-12.v42' as const;
/**
 * 章単位の分割生成の契約。v42 と同じ節構成・引用規則・検査を通すが、章ごとに
 * 生成呼び出しを分ける。単発生成と区別できるよう版を分け、生成記録に残す。
 */
export const CURRENT_CHUNKED_TEXT_PROMPT_VERSION = 'PS-TEXT-2026-09-12.v42.chunked' as const;

/**
 * 現行テキスト生成契約の版か。単発生成と分割生成は同じ節構成・引用規則・検査を
 * 通すため、検証・再利用・配信の判定では同等に扱う。版の記録自体は区別する。
 */
export function isCurrentTextPromptVersion(version: string | undefined): boolean {
  return version === CURRENT_TEXT_PROMPT_VERSION || version === CURRENT_CHUNKED_TEXT_PROMPT_VERSION;
}
export const CONTENT_REVIEW_VERSION = 'PS-CONTENT-REVIEW-2026-09-14.v16' as const;

/**
 * 保存済みデータとの互換性を含むメディア種別。現在生成・公開するのは text だけ。
 */
export type Medium = 'text' | 'audio' | 'video';
export const MEDIA: readonly Medium[] = ['text'];

/**
 * 情報区分。利用者向け画面ではこの3種類に必ず分けて表示する。
 * 「推論」という語は利用者向け文言に使わない（config/verification.json で機械的に禁止）。
 */
export type Provenance = 'paper' | 'external' | 'editorial';
export const PROVENANCE_LABELS: Record<Provenance, string> = {
  paper: '原論文に記載',
  external: '外部資料で確認',
  editorial: '考察・応用案',
};

/**
 * 音声・動画の冒頭で必ず読み上げる、AI 生成であることの断り。
 *
 * **画面の注記は音声・動画では読まれない。** 目で見る面（フッタ・論文詳細）に
 * 出しているだけでは、音声だけを聞いた人・動画だけを見た人には届かない。
 * そこで台本の先頭に固定文として入れ、読み上げと字幕の両方に必ず載せる。
 *
 * これは生成物ではなく定型文なので、`src/core/verify.ts` の
 * 「原論文に無い語を持ち込んでいないか」の照合からは除外する
 * （照合対象にすると、この文自体が原論文に無いという理由で毎回落ちる）。
 */
export const MEDIA_AI_DISCLOSURE = 'この解説はAIが論文をもとに作成したものです。正確なところは原論文をご確認ください。';

/** 確認状態。確認できないものは断定せず、未確認/要確認として表示する。 */
export type Confidence = 'confirmed' | 'unverified' | 'needs_check';

/**
 * 公開フロー。下書き → 公開。
 *
 * **人による確認の工程は持たない。** 解説は AI が生成しており、そのことを
 * 利用者に明示する（利用規約 第1条・各解説の冒頭・音声/動画の冒頭）。
 * 明示したうえで、機械的な検証（`src/core/verify.ts`）を通ったものを配信する。
 *
 * - `draft`   … 検証を通っていない。配信しない。
 * - `published` … 検証を通った。配信対象。
 *
 * 以前は `auto_verified`（機械は通したが人がまだ）と `human_verified` があったが、
 * 人が確認する工程を廃止したので、その間の状態は行き先が無くなった。
 * 過去に保存した値は `normalizePublicationStatus` で読み込み時に読み替える。
 */
export type PublicationStatus = 'draft' | 'published';
export const PUBLICATION_STATUSES: readonly PublicationStatus[] = ['draft', 'published'];

/**
 * 人による確認があった頃の状態名。保存済みデータを読むときだけ使う。
 *
 * **どちらも `draft` に落とす（fail-closed）。**
 * `auto_verified` は「昔の版の検証を通った」だけで、いまの検証を通った保証がない。
 * しかも当時は dry-run と live の両方がこの状態を書いていたので、
 * `published` へ送ると **dry-run の産物を配信対象へ昇格させてしまう**
 * （この変更で入れた dry-run 隔壁を、読み替えが横から素通りさせることになる）。
 * `human_verified` も同じで、人が見たことは新しい規則では配信の条件ではない。
 *
 * 落としたことは黙って起きない。`publish` が除外一覧に status つきで出す。
 */
const LEGACY_STATUS_MAP: Record<string, PublicationStatus> = {
  auto_verified: 'draft',
  human_verified: 'draft',
};

/**
 * 保存済みの状態を現行の語彙へ読み替える。
 *
 * **保存済み JSON も D1 の行も型検査を受けない**（`JSON.parse(...) as StoredPaper`、
 * `row.status as Paper['status']`）。読み替えずに比較すると、古い値がどの分岐にも
 * 当たらないまま扱われ、画面の表示も配信の判定も意図しない側に倒れる。
 */
export function normalizePublicationStatus<T extends string>(value: T): T | PublicationStatus {
  return LEGACY_STATUS_MAP[value] ?? value;
}

/** 処理状態。日次処理の再実行判定に使う。 */
export type ProcessingState = 'pending' | 'running' | 'done' | 'failed' | 'skipped' | 'held';

// ---------------------------------------------------------------------------
// 分野
// ---------------------------------------------------------------------------

export interface CategoryDiscovery {
  /** OpenAlex の filter 式。例: "primary_topic.subfield.id:subfields/1702" */
  openAlexFilters: string[];
  openAlexSearch: string[];
  semanticScholarFields: string[];
  semanticScholarQueries: string[];
  arxivCategories: string[];
}

export interface Category {
  code: string;
  name: string;
  nameEn: string;
  slug: string;
  order: number;
  active: boolean;
  accent: string;
  summary: string;
  discovery: CategoryDiscovery;
  /** その分野で必ず確認する観点。生成プロンプトと検証の両方で使う。 */
  rubric: string;
  /** 応用可能性を語るときの視点。 */
  applicationLens: string;
  /** その分野で特に避けるべき表現・誇張。 */
  guardrail: string;
}

// ---------------------------------------------------------------------------
// 出典
// ---------------------------------------------------------------------------

/** 原論文本文の中の位置。原文を確認せずに書かれた記述を残さないための最小単位。 */
export interface PaperLocator {
  kind: 'paper';
  /** 取得した本文スナップショットの ID。 */
  snapshotId: string;
  /** セクション名（取得できた場合）。 */
  section?: string;
  /** スナップショット本文中の文字オフセット。 */
  charStart: number;
  charEnd: number;
  /** 実際に引用した原文。改変しない。 */
  quote: string;
}

/** 外部資料。一次資料・公式情報・査読論文を優先する。 */
export interface ExternalSource {
  kind: 'external';
  url: string;
  title: string;
  publisher: string;
  /** 確認した日（ISO 8601 日付）。 */
  checkedAt: string;
  /** 該当箇所の引用（任意だが強く推奨）。 */
  quote?: string;
}

export type SourceRef = PaperLocator | ExternalSource;

// ---------------------------------------------------------------------------
// 関連資料（原論文に記載のリンク）
// ---------------------------------------------------------------------------

/**
 * 原論文の本文に書かれているコード・データ・モデルなどのリンク。
 *
 * **生成モデルには作らせない。** 原文スナップショットの文字列から決定的に抽出し、
 * どの位置に書かれていたかを `source` で必ず示す（「原論文に記載」の区分で表示する）。
 * リンク先の内容や生存は保証しない。表示側は外部リンクであることを明示する。
 */
export type RelatedResourceKind = 'code' | 'data' | 'model' | 'package' | 'other';

export interface RelatedResource {
  url: string;
  kind: RelatedResourceKind;
  /** 表示用の短い名前（ホスト名やリポジトリ名）。URL から機械的に作る。 */
  label: string;
  /** 原文中でこの URL が現れた位置。 */
  source: PaperLocator;
}

// ---------------------------------------------------------------------------
// 概念図（論文固有の仕組みを描く構造化データ）
// ---------------------------------------------------------------------------

/**
 * 論文の仕組みを「要素と関係」で表す小さな図。
 *
 * 7節の CSS カード（論文全体を1枚で）は本文の再配置であって論文固有の絵ではない。
 * ここでは生成モデルに要素（ノード）と関係（辺）を出させ、**各ノードは原文引用を必ず持つ**。
 * 引用が原文に無いノードは落とし、残ったノードが下限を割れば図全体を出さない（fail-closed）。
 * 描画は決定論的（乱数・画面幅に依存しない配置を core で計算する）。
 */
export type ConceptDiagramKind = 'flow' | 'cycle' | 'compare';
export type ConceptDiagramRole = 'input' | 'process' | 'output' | 'evidence' | 'comparison' | 'limitation';

export interface ConceptDiagramNode {
  /** 図の中で一意な短い ID（英数字とハイフン）。 */
  id: string;
  /** 利用者に見せる短い日本語ラベル。上限は config/verification.json の conceptDiagram。 */
  label: string;
  role: ConceptDiagramRole;
  /** ラベルを補う一文（任意）。 */
  note?: string;
  /** このノードの根拠になる原文位置。1件以上必須。 */
  sources: SourceRef[];
}

export interface ConceptDiagramEdge {
  from: string;
  to: string;
  /** 関係の説明（任意、短く）。 */
  label?: string;
}

export interface ConceptDiagram {
  title: string;
  kind: ConceptDiagramKind;
  nodes: ConceptDiagramNode[];
  edges: ConceptDiagramEdge[];
  /** 図の読み方を1〜2文で。 */
  caption: string;
  /** 図は原論文の記載だけから作る。考察は載せない。 */
  provenance: 'paper';
  confidence: Confidence;
}

// ---------------------------------------------------------------------------
// 数式
// ---------------------------------------------------------------------------

export interface EquationSymbol {
  symbol: string;
  meaning: string;
  /** 単位。無次元なら "無次元"。不明なら未設定にし、confidence を needs_check にする。 */
  unit?: string;
}

export interface Equation {
  id: string;
  /** LaTeX。抽出に失敗した場合は未設定にし、推測で補完しない。 */
  latex?: string;
  /** 抽出状態。needs_check のときは画面に「要確認」を表示する。 */
  extraction: Confidence;
  symbols: EquationSymbol[];
  /** 式が成り立つ前提。 */
  assumptions: string[];
  /** 論文内での役割。 */
  role: string;
  /** 初心者向けの直感的説明。 */
  intuition: string;
  /** 中級者向けの利用方法。 */
  usage: string;
  /** 専門家向けの厳密な説明。 */
  rigorous: string;
  sources: SourceRef[];
}

// ---------------------------------------------------------------------------
// 本文ブロック
// ---------------------------------------------------------------------------

export type BlockKind =
  | 'heading'
  | 'paragraph'
  | 'list'
  | 'equation'
  | 'callout'
  | 'table'
  | 'takeaways';

export interface ContentBlock {
  id: string;
  kind: BlockKind;
  /** 情報区分。すべてのブロックに必須。 */
  provenance: Provenance;
  confidence: Confidence;
  /** heading / paragraph / callout の本文。 */
  text?: string;
  /** list / takeaways の項目。 */
  items?: string[];
  /** table の行。先頭行をヘッダとして扱う。 */
  rows?: string[][];
  /** kind='equation' のときに参照する Equation の id。 */
  equationId?: string;
  /** heading のレベル（2 または 3）。 */
  headingLevel?: 2 | 3;
  sources: SourceRef[];
}

/** 本文の該当語で開く説明。本文と同じ出典・数値・情報区分検査を通す。 */
export interface InlineGlossaryEntry {
  term: string;
  aliases: string[];
  definition: string;
  context: string;
  provenance: Provenance;
  confidence: Confidence;
  sources: SourceRef[];
}

// ---------------------------------------------------------------------------
// 論文
// ---------------------------------------------------------------------------

export interface PaperAuthor {
  name: string;
  affiliation?: string;
  orcid?: string;
}

export interface PaperIdentifiers {
  doi?: string;
  arxivId?: string;
  openAlexId?: string;
  semanticScholarId?: string;
  pmid?: string;
  pmcid?: string;
}

/** 集計対象の正本ID。題名の類似だけでは別論文を統合しない。 */
export interface PaperMetricTarget {
  paperId: string;
  canonicalKey: string;
  identifiers: PaperIdentifiers;
  title: string;
  firstAuthor?: string;
  year?: number;
}

/** 論文単位の引用観測。null は未取得、0 は取得元が返した実測のゼロ。 */
export interface PaperMetricObservation {
  paperId: string;
  canonicalKey: string;
  provider: 'openalex';
  status: 'ok' | 'not_found' | 'identifier_missing' | 'identifier_mismatch' | 'ambiguous' | 'fetch_failed';
  requestedIdentifier?: string;
  openAlexId?: string;
  observedAt: string;
  providerUpdatedAt: string | null;
  publicationYear: number | null;
  workType: string | null;
  primarySubfield: { id: string; name: string } | null;
  citedByCount: number | null;
  countsByYear: Array<{ year: number; citedByCount: number }> | null;
  fwci: number | null;
  /** OpenAlex の同年・同文書種別・同subfieldで補正された値（0–1）。 */
  citationNormalizedPercentile: {
    value: number;
    isInTop1Percent: boolean | null;
    isInTop10Percent: boolean | null;
  } | null;
  previousSuccessfulObservedAt: string | null;
  /** 収録内容の訂正による負値も保持する。引用した論文の新規出版数ではない。 */
  citedByCountDelta: number | null;
}

export interface PaperMetricAlias {
  paperId: string;
  canonicalKey: string;
  matchedIdentifierKeys: string[];
}

export interface PaperMetricLedgerEntry {
  schemaVersion: 1;
  target: PaperMetricTarget;
  aliases: PaperMetricAlias[];
  observations: PaperMetricObservation[];
}

export interface PaperMetricRankingEntry {
  paperId: string;
  canonicalKey: string;
  aliasPaperIds: string[];
  rank: number | null;
  citationNormalizedPercentile: number | null;
  observedAt: string | null;
}

export interface PaperMetricRanking {
  schemaVersion: 1;
  metric: 'citation_normalized_percentile';
  direction: 'descending';
  computedAt: string;
  entries: PaperMetricRankingEntry[];
}

export interface PaperMetricRefreshReport {
  startedAt: string;
  completedAt: string;
  totalTargets: number;
  refreshed: number;
  skippedFresh: number;
  successful: number;
  ranked: number;
  deferredUntil: string | null;
  remaining: number;
  aliases: number;
  statusCounts: Record<PaperMetricObservation['status'], number>;
}

export interface PaperMetricCooldown {
  schemaVersion: 1;
  stoppedAt: string;
  retryAt: string;
  remainingPaperIds: string[];
}

export interface PaperLicense {
  /** SPDX 風の識別子（OpenAlex/Unpaywall の値をそのまま保持）。 */
  id?: string;
  label: string;
  oaStatus?: string;
  isFreeToRead: boolean;
  /** ライセンス情報の取得元 URL。 */
  sourceUrl?: string;
}

export interface RetractionInfo {
  retracted: boolean;
  withdrawn: boolean;
  corrections: Array<{ type: string; url?: string; note?: string }>;
  /** 判定に使った情報源。 */
  sources: ExternalSource[];
  /**
   * 撤回・訂正の照会が**成立した**日時。
   *
   * **照会できなかったときは入れない。** DOI が無い論文や、Crossref が
   * 落ちている間に作った論文にこれを入れると、画面の判定
   * （`apps/worker/src/shape.ts` の `integrityFlagOf`）が「確認済み」を返し、
   * 撤回済みの論文が「撤回・訂正なしを確認」の印つきで配信される。
   * 人が1件ずつ見る工程は無いので、ここで嘘を書くと誰も直さない。
   */
  checkedAt?: string;
}

/** 選定スコアの内訳。利用者にも「なぜ選ばれたか」を説明できるよう全て保存する。 */
export interface SelectionRecord {
  /** 0-100 に正規化した総合スコア。 */
  score: number;
  mode: string;
  modeLabel: string;
  axes: Array<{
    axis: string;
    raw: number;
    weight: number;
    contribution: number;
    detail: Record<string, string | number>;
  }>;
  /** 利用者向けの選定理由（日本語）。 */
  reasons: string[];
  cohortSize: number;
  rank: number;
  computedAt: string;
}

/**
 * 日次ランキングではなく、既存論文の引用関係から収載した記録。
 * 存在しない score / rank を埋めず、どの中心論文から辿ったかだけを残す。
 */
export interface CitationBackfillSelectionRecord {
  kind: 'citation_backfill';
  mode: 'citation_backfill';
  modeLabel: '引用関係から収載';
  reasons: string[];
  computedAt: string;
  sourceCenterKeys: string[];
}

export type PaperSelectionRecord = SelectionRecord | CitationBackfillSelectionRecord;

/** 参考文献 / 被引用のエッジ。 */
export interface CitationEdge {
  id: string;
  /** 引用する側（この論文が cited を参照している）。 */
  fromKey: string;
  /** 引用される側。 */
  toKey: string;
  direction: 'references' | 'cited_by';
  /** エッジの取得元。 */
  source: 'openalex' | 'crossref' | 'semantic_scholar' | 'arxiv' | 'manual';
  /** 中心論文からの距離。 */
  depth: number;
}

/** 引用グラフのノード。internalはローカル収載を表し、解説の生成・公開済みを保証しない。 */
export interface CitationNode {
  key: string;
  title: string;
  authors: string[];
  year?: number;
  venue?: string;
  citationCount?: number;
  categoryCode?: string;
  /** PaperStock 内に論文メタデータがあるか。AI解説の完成・公開状態は別に確認する。 */
  internal: boolean;
  /** internal=true のときの PaperStock 論文 ID。 */
  paperId?: string;
  identifiers: PaperIdentifiers;
  url?: string;
}

export interface CitationGraph {
  centerKey: string;
  nodes: CitationNode[];
  edges: CitationEdge[];
  /**
   * 取得できなかった方向があれば明示する。
   * `depth2` は「深さ2を全部は取り切れていない」ことを表す。
   */
  truncated: { references: boolean; citedBy: boolean; depth2?: boolean };
  /**
   * 実際に辿れた最大の深さ。辺の depth の最大値と一致する。
   * 省略されている場合は 1（深さ2を導入する前に保存したグラフ）。
   */
  maxDepth?: number;
  /**
   * 深さ2を辿らなかった・途中で打ち切った理由。
   * 「深さ2の引用が無い」と「深さ2を辿っていない」を混同しないために残す。
   */
  depthNotes?: string[];
  fetchedAt: string;
}

/**
 * 引用グラフが無いときの理由。Worker が `graph: null` に添えて返し、Web が文言を決める。
 *
 * - no_graph_stored … 収集時にグラフを保存できていない（照会の失敗など）
 * - no_edges        … 保存はしたが、参考文献・被引用のどちらも 0 件だった
 *
 * 旧 Worker は理由を付けずに `{ graph: null }` だけを返すので、受け側では省略可。
 * Worker と Web で別々に文字列を書くと片方の改名に気付けないため、ここを正本にする。
 */
export type CitationGraphEmptyReason = 'no_graph_stored' | 'no_edges';

/** 原文の取得範囲。要旨のみの場合に「全文確認済み」と表示しないための情報。 */
export type TextSourceScope = 'full_text' | 'abstract_only' | 'none';

export interface PrimarySnapshot {
  id: string;
  /** 取得元 URL。system.json の primaryTextAllowedHosts に含まれるホストのみ。 */
  url: string;
  contentType: string;
  /** 本文（プレーンテキスト化後）。 */
  text: string;
  /** プレーンテキスト化後の本文の SHA-256。 */
  sha256: string;
  /** プレーンテキスト化後の本文の UTF-8 バイト数。 */
  bytes: number;
  fetchedAt: string;
  scope: TextSourceScope;
  /**
   * HTML/JATS の取得元が持つ数式を、本文の [EQ:n] と結び付ける決定的な抽出記録。
   * AI の出力ではなく、原文パーサーが取得時に作った値だけを保存する。
   */
  equationExtraction?: {
    /** 数式を抽出した取得元バイト列の SHA-256。 */
    sourceSha256: string;
    /** [EQ:n] を含む、この snapshot.text の SHA-256。 */
    textSha256: string;
    /** [EQ:0] から順に対応する、取得元から抽出した LaTeX。 */
    latex: string[];
  };
}

export interface Paper {
  /** PaperStock 内部 ID。canonicalKey から決定的に生成する。 */
  id: string;
  /** 重複排除に使う正規化キー。DOI > arXiv > OpenAlex > 正規化タイトル の優先順。 */
  canonicalKey: string;
  title: string;
  /** 日本語タイトル。原題を置き換えない。 */
  titleJa?: string;
  authors: PaperAuthor[];
  publishedDate?: string;
  year?: number;
  venue?: string;
  identifiers: PaperIdentifiers;
  urls: { landing?: string; pdf?: string; html?: string };
  license: PaperLicense;
  categoryCode: string;
  abstract: string;
  citationCount?: number;
  citationCountFetchedAt?: string;
  /** 参考文献。取得できた範囲のみ。 */
  references: CitationNode[];
  /** 被引用。取得できた場合のみ。 */
  citedBy: CitationNode[];
  integrity: RetractionInfo;
  selection: PaperSelectionRecord;
  textSourceScope: TextSourceScope;
  snapshots: PrimarySnapshot[];
  /** 原文に書かれていたコード・データ等のリンク。原文スナップショットから決定的に抽出する。 */
  relatedResources?: RelatedResource[];
  status: PublicationStatus;
  processing: ProcessingState;
  /** 日次処理の実行日（JST の YYYY-MM-DD）。 */
  runDate: string;
  createdAt: string;
  updatedAt: string;
  /** Notion のページ ID（同期後に設定）。 */
  notionPageId?: string;
}

// ---------------------------------------------------------------------------
// コンテンツ資産（現在は1論文 x beginner x text = 1件。旧レベル・メディアも読み込み互換を保つ）
// ---------------------------------------------------------------------------

export interface VerificationFinding {
  checkId: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  blockId?: string;
  /** materialize後の用語・数式等。contentReviewTargetsと同じ識別子で、raw配列位置ではない。 */
  targetId?: string;
  /** 該当箇所の抜粋。 */
  excerpt?: string;
}

export interface VerificationResult {
  passed: boolean;
  findings: VerificationFinding[];
  checkedAt: string;
}

/** AIによる別呼出の意味照合。人による確認・正しさの保証ではない。 */
export interface ContentReviewJudgement {
  id: string;
  verdict: 'supported' | 'unsupported' | 'uncertain';
  reason: string;
}

/** AI照合の修理候補。原文位置は未検証で、保存する照合証跡や支持判定とは分離する。 */
export interface ContentReviewEvidenceRepair {
  targetId: string;
  quotes: string[];
}

export interface ContentReview {
  version: string;
  contentSha256: string;
  sourceSha256: string;
  /** 実際に提示した原文抜粋を含む文脈のハッシュ。 */
  contextSha256: string;
  provider: string;
  model: string;
  checkedAt: string;
  judgements: ContentReviewJudgement[];
  coverage: { verdict: 'supported' | 'unsupported' | 'uncertain'; reason: string };
}

export interface SubtitleCue {
  index: number;
  startMs: number;
  endMs: number;
  text: string;
}

export interface ContentAsset {
  id: string;
  paperId: string;
  level: Level;
  medium: Medium;
  status: PublicationStatus | 'pending_generation' | 'failed';
  /** medium='text' の本文。ブロック単位で情報区分を持つ。 */
  blocks?: ContentBlock[];
  glossary?: InlineGlossaryEntry[];
  /** テキストで参照する数式。 */
  equations?: Equation[];
  /** 旧保存形式の互換用。現行の要点は先頭5ブロックに一本化し、ここは空にする。 */
  takeaways?: string[];
  /**
   * 論文固有の概念図。原文引用を持つノードだけで構成する。
   * 無い・検査に落ちた場合は未設定にする。要点を重複する図は生成しない。
   */
  diagram?: ConceptDiagram;
  /** medium='audio' | 'video' の台本。承認済みテキストから一方向に派生させる。 */
  script?: string;
  /** 動画の字幕（WebVTT 用の cue 配列）。 */
  subtitles?: SubtitleCue[];
  /** 音声・動画の文字起こし。 */
  transcript?: string;
  /** 生成物のURL（R2 等）。 */
  fileUrl?: string;
  fileSha256?: string;
  durationSec?: number;
  /** 派生元のテキスト資産 ID。音声・動画では必須。 */
  derivedFromAssetId?: string;
  /** 生成に必要だが未設定の環境変数など。status=pending_generation のとき必ず埋める。 */
  missingConfig: string[];
  verification?: VerificationResult;
  contentReview?: ContentReview;
  /** 生成に使ったモデル・プロバイダ。音声・動画では**台本**を作ったモデルを指す。 */
  generator?: { provider: string; model?: string; promptVersion: string };
  /**
   * 音声合成の記録。台本を作ったモデル（generator）とは別のサービスなので分けて持つ。
   * characters は実際に送った文字数で、音声合成の費用はここから出る。
   */
  speech?: { provider: string; voice: string; format: string; characters: number; requests: number };
  createdAt: string;
  updatedAt: string;
  notionPageId?: string;
}

// ---------------------------------------------------------------------------
// 日次処理
// ---------------------------------------------------------------------------

export interface CategoryRunResult {
  categoryCode: string;
  state: ProcessingState;
  /** journal の dry-run / live を識別する。未設定は mode 分離前の保存データ。 */
  runMode?: DailyRun['mode'];
  paperId?: string;
  /** 同日・分野をまたいだ重複判定に使う正規化キー。 */
  paperCanonicalKey?: string;
  paperTitle?: string;
  mode?: string;
  /** 失敗・保留の理由コード。 */
  reasons: string[];
  error?: string;
  startedAt: string;
  finishedAt?: string;
  /** 概算コスト（円）。 */
  costJpy?: number;
  /**
   * costJpy がプロバイダの実測トークン数ではなく、送受信テキストからの推定に基づく場合に true。
   * 表示側は「推定」と明示すること。
   */
  costEstimated?: boolean;
  /** 生成したコンテンツ資産の件数。 */
  assetCount?: number;
  /**
   * フェーズ別の所要時間（ミリ秒）。キーは selectMs / fetchMs / generateMs / verifyMs / notionMs など。
   * 決定性の規則は内容にだけ適用され、時刻の計測は対象外。
   */
  phases?: Record<string, number>;
}

export interface DailyRun {
  id: string;
  runDate: string;
  mode: 'dry-run' | 'live';
  startedAt: string;
  finishedAt?: string;
  state: ProcessingState;
  results: CategoryRunResult[];
  totals: {
    categories: number;
    succeeded: number;
    failed: number;
    skipped: number;
    assets: number;
    costJpy: number;
    /** costJpy に推定値が含まれる場合に true。 */
    costEstimated: boolean;
    elapsedMs: number;
  };
  /** 再実行の元になった run ID。 */
  retryOf?: string;
  notionPageId?: string;
}

// ---------------------------------------------------------------------------
// 公開 API のビュー型（Worker と Web が共有する）
// ---------------------------------------------------------------------------

export interface VisualSummaryItem {
  label: string;
  text: string;
}

export interface FeedItem {
  paperId: string;
  slug: string;
  title: string;
  titleJa?: string;
  categoryCode: string;
  categoryName: string;
  categoryAccent: string;
  runDate: string;
  year?: number;
  venue?: string;
  authors: string[];
  /** この論文が明らかにしたこと（1文）。 */
  headline: string;
  selectionReasons: string[];
  selectionMode: string;
  citationCount?: number;
  license: PaperLicense;
  textSourceScope: Paper['textSourceScope'];
  integrityFlag: 'ok' | 'retracted' | 'withdrawn' | 'corrected' | 'unchecked';
  availableAssets: Array<{ level: Level; medium: Medium; status: string }>;
  /** 公開可能で、5要素すべてを自動検証できた「30秒でつかむ」だけを返す。 */
  visualSummary?: VisualSummaryItem[];
}

export interface PaperDetail {
  paper: Paper;
  category: Category;
  headline: string;
  /** なぜ重要か。 */
  significance: string;
  assets: ContentAsset[];
  graph: CitationGraph;
}

// ---------------------------------------------------------------------------
// Notion -> D1 公開同期
// ---------------------------------------------------------------------------

export const PUBLIC_CONTENT_SYNC_SCHEMA_VERSION = 'paperstock-public-content-sync/2' as const;
export const PUBLIC_CITATION_CONTENT_SYNC_SCHEMA_VERSION = 'paperstock-public-citation-content-sync/1' as const;

/** 原文スナップショットやローカルパスを含まない、D1 配信用の論文。 */
export interface PublicContentPaper {
  id: string;
  canonicalKey: string;
  title: string;
  titleJa?: string;
  authors: PaperAuthor[];
  publishedDate?: string;
  year?: number;
  venue?: string;
  identifiers: PaperIdentifiers;
  urls: { landing?: string; pdf?: string; html?: string };
  license: PaperLicense;
  categoryCode: string;
  abstract: string;
  citationCount?: number;
  integrity: RetractionInfo;
  selection: PaperSelectionRecord;
  textSourceScope: TextSourceScope;
  /** 原文に書かれていたコード・データ等のリンク。 */
  relatedResources?: RelatedResource[];
  status: 'published';
  processing: 'done';
  runDate: string;
  createdAt: string;
  updatedAt: string;
}

/** 現在の公開経路はテキスト専用。メディアを再開するときに明示的に拡張する。 */
export interface PublicContentTextAsset {
  id: string;
  paperId: string;
  level: Level;
  medium: 'text';
  status: 'published';
  blocks: ContentBlock[];
  glossary?: InlineGlossaryEntry[];
  equations: Equation[];
  takeaways: string[];
  /** 検査を通った概念図だけを載せる。 */
  diagram?: ConceptDiagram;
  missingConfig: [];
  verification: VerificationResult & { passed: true };
  contentReview?: ContentReview;
  generator?: { provider: string; model?: string; promptVersion: string };
  createdAt: string;
  updatedAt: string;
}

export interface PublicContentStoredPaper {
  paper: PublicContentPaper;
  slug: string;
  headline: string;
  significance: string;
  graph: CitationGraph;
}

export interface PublicContentSyncCategory {
  code: string;
  name: string;
  nameEn: string;
  slug: string;
  order: number;
  accent: string;
  summary: string;
}

export interface PublicContentSyncPayload {
  schemaVersion: typeof PUBLIC_CONTENT_SYNC_SCHEMA_VERSION;
  runId: string;
  runDate: string;
  categories: PublicContentSyncCategory[];
  papers: Array<{
    storedPaper: PublicContentStoredPaper;
    assets: PublicContentTextAsset[];
  }>;
}

/** Notion で読み戻し監査した引用収載論文を、D1へ追加する小分けpayload。 */
export interface PublicCitationContentSyncPayload {
  schemaVersion: typeof PUBLIC_CITATION_CONTENT_SYNC_SCHEMA_VERSION;
  batchId: string;
  papers: Array<{
    storedPaper: PublicContentStoredPaper;
    assets: PublicContentTextAsset[];
  }>;
}

// ---------------------------------------------------------------------------
// 公開後の再検査と取り下げ（D1 の配信キャッシュに遡及する唯一の経路）
// ---------------------------------------------------------------------------

/**
 * 公開後の再検査。
 *
 * 取得時の検査は「取れたか」を見るだけで、公開後に原論文が削除・移動・撤回されても
 * 誰も気付けなかった（Zenodo の HTTP 410 が公開中のまま残った実例がある）。
 * そこで、配信中の論文を Worker の在庫照会で列挙し、原文 URL・Zenodo record API・
 * arXiv API・Crossref を再照会して、確定した削除・撤回だけを取り下げる。
 *
 * - 一時的な失敗（5xx・タイムアウト）は「未確認」として記録し、取り下げない。
 * - 404 は1回では確定させず、連続して観測した回数が config の回数に達したときだけ確定する。
 *   410 は削除の明示なので1回で確定する。
 * - 要旨しか取得できていない論文（textSourceScope ≠ full_text）は、原文が生きていても
 *   現行方針では配信できないので取り下げる（理由 full_text_required）。
 */
export const PUBLIC_CONTENT_INVENTORY_SCHEMA_VERSION = 'paperstock-public-content-inventory/1' as const;
export const PUBLIC_CONTENT_UNPUBLISH_SCHEMA_VERSION = 'paperstock-public-content-unpublish/1' as const;
export const PUBLIC_CONTENT_LINKS_SCHEMA_VERSION = 'paperstock-public-content-links/1' as const;
export const SOURCE_RECHECK_SCHEMA_VERSION = 'paperstock-source-recheck/1' as const;

/** 取り下げの理由。画面には出さず、journal・Notion・監査応答に残す。 */
export type TakedownReason =
  | 'source_removed'
  | 'arxiv_withdrawn'
  | 'retracted'
  | 'full_text_required'
  | 'operator';

export type SourceRecheckStatus =
  | 'available'
  | 'removed'
  | 'withdrawn'
  | 'retracted'
  | 'full_text_required'
  | 'unverified';

/** Worker が返す、配信中の論文1件の最小情報。本文・原文は含めない。 */
export interface PublicContentInventoryItem {
  id: string;
  slug: string;
  canonicalKey: string;
  title: string;
  categoryCode: string;
  runDate: string;
  textSourceScope: TextSourceScope;
  urls: { landing?: string; pdf?: string; html?: string };
  identifiers: PaperIdentifiers;
  selectionKind: 'daily' | 'citation_backfill';
  integrity: { retracted: boolean; withdrawn: boolean; checkedAt?: string };
  updatedAt: string;
}

export interface PublicContentInventoryPayload {
  schemaVersion: typeof PUBLIC_CONTENT_INVENTORY_SCHEMA_VERSION;
  generatedAt: string;
  papers: PublicContentInventoryItem[];
}

/** ID 指定で D1 のコンテンツ3表（papers / assets / citation_edges）から行を消す。会員表には触れない。 */
export interface PublicContentUnpublishPayload {
  schemaVersion: typeof PUBLIC_CONTENT_UNPUBLISH_SCHEMA_VERSION;
  batchId: string;
  checkedAt: string;
  papers: Array<{
    paperId: string;
    reason: TakedownReason;
    /** 判定の根拠（URL と HTTP 状態など）。秘密情報を含めない。 */
    evidence: string;
  }>;
}

export interface PublicContentUnpublishResponse {
  ok: true;
  batchId: string;
  sha256: string;
  requested: number;
  deleted: { papers: number; assets: number; citationEdges: number };
}

/** 既存行の URL 列だけを補正する（HTML 専用列の導入時の移行と、リンク再検査の結果反映に使う）。 */
export interface PublicContentLinksPayload {
  schemaVersion: typeof PUBLIC_CONTENT_LINKS_SCHEMA_VERSION;
  batchId: string;
  papers: Array<{
    paperId: string;
    urls: { landing?: string; pdf?: string; html?: string };
    relatedResources?: RelatedResource[];
  }>;
}

export interface PublicContentLinksResponse {
  ok: true;
  batchId: string;
  sha256: string;
  requested: number;
  updated: number;
}

export interface SourceRecheckEvidence {
  url: string;
  httpStatus?: number;
  finalUrl?: string;
  note: string;
}

export interface SourceRecheckPaperResult {
  paperId: string;
  slug: string;
  title: string;
  status: SourceRecheckStatus;
  reason?: TakedownReason;
  evidence: SourceRecheckEvidence[];
  checkedAt: string;
  /** 404 を連続して観測した回数。config の回数に達したら removed へ確定する。 */
  consecutiveMissing: number;
}

export interface SourceRecheckReport {
  schemaVersion: typeof SOURCE_RECHECK_SCHEMA_VERSION;
  mode: 'dry-run' | 'live';
  /** ok = 取り下げ対象なし。action_required = 対象あり（dry-run では未適用）。failed = 実行できなかった。 */
  status: 'ok' | 'action_required' | 'failed';
  checkedAt: string;
  inventory: { papers: number; source: 'worker' | 'file' };
  results: {
    available: number;
    removed: number;
    withdrawn: number;
    retracted: number;
    fullTextRequired: number;
    unverified: number;
  };
  takedown: {
    planned: number;
    applied: number;
    papers: Array<{ paperId: string; slug: string; reason: TakedownReason }>;
    /** 取り下げ計画の出どころ。journal = 直前の再検査の記録、inventory = 在庫からの再照会。 */
    source: 'journal' | 'inventory';
    /** 計画全体の決定的 ID。分割せず送れる件数なら Worker へ送った batchId と同じ。 */
    batchId?: string;
    /** source='journal' のとき、読み直した記録の checkedAt。 */
    sourceCheckedAt?: string;
    /** source='journal' のとき、読み直した記録のパス（リポジトリ相対）。 */
    sourceJournalPath?: string;
    /**
     * 要求したのに D1 で削除されなかった件数（既に行が無かった）。
     * 「Worker へ届かなかった」とは別なので error にはしない。修理の合図でもない。
     */
    alreadyMissing?: number;
    /** wrangler d1 execute で手動適用できる同内容の SQL。live で Worker へ送れないときの復旧経路。 */
    sqlPath?: string;
    error?: string;
  };
  links: { planned: number; applied: number; error?: string };
  notion: { updated: number; error?: string };
  journalPath: string;
  nextAction: string;
  /** 実行条件の但し書き（404 の確定条件・journal からの読み直しなど）。 */
  note?: string;
  error?: string;
}

/** 公開解説のリクエスト。連絡先・氏名は収集しない。 */
export interface PaperExplanationRequest {
  identifier: string;
  question: string;
  consent: boolean;
  website: string;
}

export interface QueuedPaperRequest {
  id: string;
  identifier: string;
  question: string;
  createdAt: number;
}

export interface PaperRequestReceipt {
  ok: true;
  id: string;
  message: string;
}
